import { useEffect, useState } from 'react';
import {
  CheckCircle,
  Circle,
  Download,
  Copy,
  ChevronDown,
  ChevronRight,
  Loader2,
  FileText,
  Building2,
  TrendingUp,
  Home,
  Receipt,
  DollarSign,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';

interface FilingSection {
  id: string;
  title: string;
  icon: any;
  description: string;
  completed: boolean;
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-muted rounded hover:bg-accent transition-smooth"
      title={`Copy ${label || value}`}
    >
      <Copy className="h-3 w-3" />
      {copied ? 'Copied!' : label || 'Copy'}
    </button>
  );
}

function AmountRow({ label, value, copyValue }: { label: string; value: string; copyValue?: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{value}</span>
        <CopyButton value={copyValue || value.replace(/[$,]/g, '')} />
      </div>
    </div>
  );
}

export function FilingGuidePage() {
  const { selectedYear, businesses, investments, fetchBusinesses, fetchInvestments } = useTaxStore();
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    income: true,
  });
  const [completedSections, setCompletedSections] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem(`filing-guide-${selectedYear}`);
    return saved ? JSON.parse(saved) : {};
  });

  // Data states
  const [taxSummary, setTaxSummary] = useState<any>(null);
  const [expensesData, setExpensesData] = useState<any>(null);
  const [incomeData, setIncomeData] = useState<any>(null);
  const [deductionsData, setDeductionsData] = useState<any>(null);
  const [documentsData, setDocumentsData] = useState<any>(null);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchBusinesses(selectedYear),
          fetchInvestments(selectedYear),
        ]);
        const [summary, expenses, income, deductions, docs] = await Promise.all([
          api.getTaxSummary(selectedYear),
          api.listExpenses({ year: selectedYear }),
          api.listIncome({ year: selectedYear }),
          api.getDeductions(selectedYear),
          api.listDocuments({ year: selectedYear }),
        ]);
        setTaxSummary(summary);
        setExpensesData(expenses);
        setIncomeData(income);
        setDeductionsData(deductions);
        setDocumentsData(docs);
      } catch (err) {
        console.error('Error loading filing data:', err);
      }
      setLoading(false);
    };
    loadAll();
  }, [selectedYear, fetchBusinesses, fetchInvestments]);

  useEffect(() => {
    localStorage.setItem(`filing-guide-${selectedYear}`, JSON.stringify(completedSections));
  }, [completedSections, selectedYear]);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleCompleted = (id: string) => {
    setCompletedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Compute expense totals by category
  const expensesByCategory: Record<string, number> = {};
  (expensesData?.expenses || []).forEach((e: any) => {
    const cat = e.category || 'other';
    expensesByCategory[cat] = (expensesByCategory[cat] || 0) + (e.amount || 0);
  });

  // Map categories to TaxAct fields
  const taxActExpenseMapping = [
    { taxActField: 'Advertising', categories: ['advertising'], key: 'advertising' },
    { taxActField: 'Office expense', categories: ['office_expense'], key: 'office' },
    { taxActField: 'Supplies', categories: ['equipment_purchase', 'supplies'], key: 'supplies' },
    { taxActField: 'Insurance (not health)', categories: ['insurance'], key: 'insurance' },
    { taxActField: 'Taxes and licenses', categories: ['taxes_licenses', 'startup_costs'], key: 'taxes' },
    { taxActField: 'Utilities', categories: ['utilities'], key: 'utilities' },
    { taxActField: 'Legal & professional services', categories: ['legal_professional'], key: 'legal' },
    { taxActField: 'Software and subscriptions (Other)', categories: ['software', 'software_subscriptions'], key: 'software' },
    { taxActField: 'Education and training (Other)', categories: ['education'], key: 'education' },
  ];

  const getAmount = (categories: string[]) =>
    categories.reduce((sum, cat) => sum + (expensesByCategory[cat] || 0), 0);

  // Income by client
  const incomeByClient: Record<string, number> = {};
  let totalIncome = 0;
  (incomeData?.income || []).forEach((i: any) => {
    const client = i.clientName || i.source || 'Other';
    incomeByClient[client] = (incomeByClient[client] || 0) + (i.amount || 0);
    totalIncome += i.amount || 0;
  });

  // Investment summary
  const stockSales = investments.filter((i: any) => i.type === 'stock' && i.transactionType === 'sell');
  const stockDividends = investments.filter((i: any) => i.type === 'stock' && i.transactionType === 'dividend');
  const cryptoSales = investments.filter((i: any) => i.type === 'crypto' && i.transactionType === 'sell');
  const totalStockProceeds = stockSales.reduce((sum: number, s: any) => sum + (s.totalValue || 0), 0);
  const totalDividends = stockDividends.reduce((sum: number, s: any) => sum + (s.totalValue || 0), 0);

  // Deductions
  const homeOffice = (deductionsData?.deductions || []).find((d: any) => d.category === 'home_office');
  const propertyTax = (deductionsData?.deductions || []).find((d: any) => d.category === 'property_tax');
  const mortgageInterest = (deductionsData?.deductions || []).find((d: any) => d.category === 'mortgage_interest');

  // W-2s
  const w2s = (documentsData?.documents || []).filter((d: any) => d.type === 'w2');

  // Form 8949 download
  const downloadForm8949 = () => {
    const allSales = [...stockSales, ...cryptoSales];
    const salesByBroker: Record<string, any[]> = {};
    allSales.forEach((s: any) => {
      const b = s.broker || s.exchange || 'Unknown';
      if (!salesByBroker[b]) salesByBroker[b] = [];
      salesByBroker[b].push(s);
    });

    const brokerLabels: Record<string, string> = {
      fidelity: 'Fidelity', schwab: 'Charles Schwab', robinhood: 'Robinhood',
      coinbase: 'Coinbase', kraken: 'Kraken', other: 'Other',
    };

    const csvLines: string[] = [];
    csvLines.push('Form 8949 - Sales and Other Dispositions of Capital Assets');
    csvLines.push(`Tax Year: ${selectedYear}`);
    csvLines.push('');

    Object.entries(salesByBroker).forEach(([brokerKey, sales]) => {
      const label = brokerLabels[brokerKey] || brokerKey;
      csvLines.push(`1099 Provider: ${label}`);
      csvLines.push('(a) Description of property,(b) Date acquired,(c) Date sold or disposed of,(d) Proceeds,(e) Cost or other basis,(f) Code,(g) Adjustment,(h) Gain or (loss)');

      let totalProceeds = 0;
      let totalCostBasis = 0;

      sales.forEach((s: any) => {
        const symbol = s.symbol || s.asset || '';
        const shares = s.shares || s.amount || 0;
        const desc = `${shares} ${symbol}`;
        const dateSold = s.date ? new Date(s.date).toLocaleDateString('en-US') : '';
        const proceeds = s.totalValue || 0;
        const costBasis = s.costBasis || 0;
        const gainLoss = costBasis > 0 ? proceeds - costBasis : '';
        totalProceeds += proceeds;
        totalCostBasis += costBasis;
        csvLines.push([`"${desc}"`, '"Various"', `"${dateSold}"`, proceeds.toFixed(2), costBasis > 0 ? costBasis.toFixed(2) : '', '', '', typeof gainLoss === 'number' ? gainLoss.toFixed(2) : ''].join(','));
      });

      csvLines.push(['"Totals"', '', '', totalProceeds.toFixed(2), totalCostBasis > 0 ? totalCostBasis.toFixed(2) : '', '', '', totalCostBasis > 0 ? (totalProceeds - totalCostBasis).toFixed(2) : ''].join(','));
      csvLines.push('');
    });

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Form_8949_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Business expenses CSV download
  const downloadExpensesCsv = () => {
    const csvLines: string[] = [];
    csvLines.push('Category,Description,Amount,Date,Vendor');
    (expensesData?.expenses || []).forEach((e: any) => {
      csvLines.push([
        `"${e.category}"`,
        `"${(e.description || '').replace(/"/g, '""')}"`,
        e.amount,
        e.date,
        `"${(e.vendor || '').replace(/"/g, '""')}"`,
      ].join(','));
    });
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Business_Expenses_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const completedCount = Object.values(completedSections).filter(Boolean).length;
  const totalSections = 8;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">TaxAct Filing Guide</h2>
        <p className="text-muted-foreground">Step-by-step guide with your {selectedYear} data ready to enter</p>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${(completedCount / totalSections) * 100}%` }}
            />
          </div>
          <span className="text-sm text-muted-foreground">{completedCount}/{totalSections} sections</span>
        </div>
      </div>

      {/* Downloads Section */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-3">Downloads for TaxAct</h3>
        <p className="text-sm text-muted-foreground mb-4">Download these files before starting. TaxAct will ask you to upload them.</p>
        <div className="flex flex-wrap gap-3">
          {stockSales.length > 0 && (
            <button
              onClick={downloadForm8949}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
            >
              <Download className="h-4 w-4" />
              Form 8949 (Stock Sales)
            </button>
          )}
          {(expensesData?.expenses || []).length > 0 && (
            <button
              onClick={downloadExpensesCsv}
              className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-accent transition-smooth"
            >
              <Download className="h-4 w-4" />
              Business Expenses CSV
            </button>
          )}
          <button
            onClick={() => api.exportExcel(selectedYear)}
            className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-accent transition-smooth"
          >
            <Download className="h-4 w-4" />
            Full Tax Export (Excel)
          </button>
        </div>
      </div>

      {/* Section 1: W-2 Income */}
      <SectionCard
        id="w2"
        title="1. W-2 Income"
        icon={FileText}
        description="Enter your W-2 forms from employers"
        completed={completedSections['w2']}
        expanded={expandedSections['w2']}
        onToggle={() => toggleSection('w2')}
        onComplete={() => toggleCompleted('w2')}
      >
        {w2s.length > 0 ? (
          <div className="space-y-2">
            {w2s.map((w2: any, i: number) => (
              <div key={i} className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">{w2.extractedData?.employer || w2.employer || `W-2 #${i + 1}`}</p>
                {w2.extractedData?.wages && (
                  <AmountRow label="Wages (Box 1)" value={formatCurrency(w2.extractedData.wages)} />
                )}
                {w2.extractedData?.federalWithheld && (
                  <AmountRow label="Federal withheld (Box 2)" value={formatCurrency(w2.extractedData.federalWithheld)} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No W-2s uploaded. Upload them in the Documents section first, or enter them directly in TaxAct from the paper forms.</p>
        )}
        <TaxActTip>Enter each W-2 in TaxAct under "Income &gt; W-2 Wages". Have your paper W-2s handy to copy all boxes.</TaxActTip>
      </SectionCard>

      {/* Section 2: Business Income (Schedule C) */}
      <SectionCard
        id="income"
        title="2. Business Income (Schedule C)"
        icon={Building2}
        description="Self-employment income for your business"
        completed={completedSections['income']}
        expanded={expandedSections['income']}
        onToggle={() => toggleSection('income')}
        onComplete={() => toggleCompleted('income')}
      >
        <AmountRow label="Total business income" value={formatCurrency(totalIncome)} />
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">By Client</p>
          {Object.entries(incomeByClient).map(([client, amount]) => (
            <AmountRow key={client} label={client} value={formatCurrency(amount)} />
          ))}
        </div>

        <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
          <p className="text-sm font-medium text-blue-500 mb-2">TaxAct Income Entry</p>
          <AmountRow label="1099-MISC (if received)" value="Enter from your 1099 forms" copyValue="" />
          <AmountRow label="Cash/Checks (not on 1099s)" value={formatCurrency(totalIncome)} />
          <p className="text-xs text-muted-foreground mt-2">Adjust the Cash/Checks amount by subtracting any income already reported on 1099s to avoid double-counting.</p>
        </div>
        <TaxActTip>In TaxAct: Business &gt; Business Income. Enter 1099s first, then remaining income under "Cash/Checks (Not reported on 1099s)".</TaxActTip>
      </SectionCard>

      {/* Section 3: Business Expenses */}
      <SectionCard
        id="expenses"
        title="3. Business Expenses (Schedule C)"
        icon={Receipt}
        description="Deductible business expenses"
        completed={completedSections['expenses']}
        expanded={expandedSections['expenses']}
        onToggle={() => toggleSection('expenses')}
        onComplete={() => toggleCompleted('expenses')}
      >
        <AmountRow
          label="Total expenses"
          value={formatCurrency((expensesData?.expenses || []).reduce((s: number, e: any) => s + (e.amount || 0), 0))}
        />

        <div className="mt-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">TaxAct Expense Fields</p>
          {taxActExpenseMapping.map((mapping) => {
            const amount = getAmount(mapping.categories);
            if (amount === 0) return null;
            return (
              <AmountRow
                key={mapping.key}
                label={mapping.taxActField}
                value={formatCurrency(amount)}
              />
            );
          })}
        </div>

        <TaxActTip>In TaxAct: Business &gt; Business Expenses. Enter standard categories first, then use "Other expenses" for Software and Education.</TaxActTip>
      </SectionCard>

      {/* Section 4: Home Office */}
      <SectionCard
        id="homeoffice"
        title="4. Home Office Deduction"
        icon={Home}
        description="Business use of home"
        completed={completedSections['homeoffice']}
        expanded={expandedSections['homeoffice']}
        onToggle={() => toggleSection('homeoffice')}
        onComplete={() => toggleCompleted('homeoffice')}
      >
        {homeOffice ? (
          <>
            <AmountRow label="Home office deduction" value={formatCurrency(homeOffice.amount)} />
            <p className="text-sm text-muted-foreground mt-2">Simplified method: 300 sq ft x $5/sq ft</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No home office deduction entered. Add it in the Deductions section if applicable.</p>
        )}
        <TaxActTip>In TaxAct: Business &gt; Business Use of Home. Select "Simplified method" and enter 300 square feet.</TaxActTip>
      </SectionCard>

      {/* Section 5: Investments */}
      <SectionCard
        id="investments"
        title="5. Investments (Schedule D / Form 8949)"
        icon={TrendingUp}
        description="Stock sales, dividends, and capital gains"
        completed={completedSections['investments']}
        expanded={expandedSections['investments']}
        onToggle={() => toggleSection('investments')}
        onComplete={() => toggleCompleted('investments')}
      >
        {stockSales.length > 0 && (
          <>
            <AmountRow label="Stock sales" value={`${stockSales.length} transactions`} copyValue={String(stockSales.length)} />
            <AmountRow label="Total proceeds" value={formatCurrency(totalStockProceeds)} />
          </>
        )}
        {stockDividends.length > 0 && (
          <AmountRow label="Dividends" value={formatCurrency(totalDividends)} />
        )}
        {cryptoSales.length > 0 && (
          <AmountRow label="Crypto sales" value={`${cryptoSales.length} transactions`} copyValue={String(cryptoSales.length)} />
        )}

        {stockSales.length > 0 && (
          <div className="mt-3">
            <button
              onClick={downloadForm8949}
              className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
            >
              <Download className="h-4 w-4" />
              Download Form 8949 CSV
            </button>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
          <p className="text-sm font-medium text-blue-500 mb-2">Form 8949 Categories</p>
          <p className="text-xs text-muted-foreground">
            <strong>A</strong> — Short-term, 1099-B includes cost basis<br />
            <strong>B</strong> — Short-term, 1099-B does NOT include cost basis<br />
            <strong>D</strong> — Long-term, 1099-B includes cost basis<br />
            <strong>E</strong> — Long-term, 1099-B does NOT include cost basis
          </p>
        </div>

        <TaxActTip>Import your Fidelity 1099-B directly into TaxAct, or enter transactions manually. Attach the Form 8949 PDF when prompted.</TaxActTip>
      </SectionCard>

      {/* Section 6: Mortgage & Property */}
      <SectionCard
        id="mortgage"
        title="6. Mortgage Interest & Property Taxes (Form 1098)"
        icon={Home}
        description="Itemized deductions from your mortgage"
        completed={completedSections['mortgage']}
        expanded={expandedSections['mortgage']}
        onToggle={() => toggleSection('mortgage')}
        onComplete={() => toggleCompleted('mortgage')}
      >
        {mortgageInterest && (
          <AmountRow label="Mortgage interest (Box 1)" value={formatCurrency(mortgageInterest.amount)} />
        )}
        {propertyTax && (
          <AmountRow label="Property taxes (Box 10)" value={formatCurrency(propertyTax.amount)} />
        )}
        {!mortgageInterest && !propertyTax && (
          <p className="text-sm text-muted-foreground">No mortgage data entered. Add it in the Deductions section if applicable.</p>
        )}
        <TaxActTip>In TaxAct: Itemized Deductions &gt; Mortgage Interest (Form 1098). Enter values from your lender's 1098 form. Property taxes go under State and Local Taxes.</TaxActTip>
      </SectionCard>

      {/* Section 7: Sales Tax */}
      <SectionCard
        id="salestax"
        title="7. State & Local Sales Tax"
        icon={DollarSign}
        description="Florida residents: deduct sales tax instead of income tax"
        completed={completedSections['salestax']}
        expanded={expandedSections['salestax']}
        onToggle={() => toggleSection('salestax')}
        onComplete={() => toggleCompleted('salestax')}
      >
        <div className="space-y-2">
          <p className="text-sm">Since Florida has no state income tax, elect to deduct <strong>sales tax</strong> instead.</p>
          <AmountRow label="Method" value="Table Amount" copyValue="Table Amount" />
          <AmountRow label="Local sales tax rate (Hillsborough)" value="1.5%" copyValue="1.5" />
        </div>
        <TaxActTip>In TaxAct: Itemized Deductions &gt; State and Local Sales Tax. Choose "Table Amount" and enter 1.5 for local rate.</TaxActTip>
      </SectionCard>

      {/* Section 8: Review & File */}
      <SectionCard
        id="review"
        title="8. Review & File"
        icon={CheckCircle}
        description="Final checks before filing"
        completed={completedSections['review']}
        expanded={expandedSections['review']}
        onToggle={() => toggleSection('review')}
        onComplete={() => toggleCompleted('review')}
      >
        <div className="space-y-3">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium mb-2">Checklist</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>- Verify all W-2 data matches your paper forms</li>
              <li>- Confirm business income totals to $12,700</li>
              <li>- Check expense categories are mapped correctly</li>
              <li>- Attach Fidelity 1099-B PDF for Form 8949</li>
              <li>- Verify mortgage interest & property tax from Form 1098</li>
              <li>- Review refund amount before filing</li>
              <li>- No IP PIN needed (TaxAct PIN is different)</li>
            </ul>
          </div>
          <div className="p-3 bg-green-500/10 rounded-lg">
            <p className="text-sm font-medium text-green-600">Expected refund: ~$5,406</p>
            <p className="text-xs text-muted-foreground">Based on your data as entered in TaxAct</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

// Reusable section card component
function SectionCard({
  id,
  title,
  icon: Icon,
  description,
  completed,
  expanded,
  onToggle,
  onComplete,
  children,
}: {
  id: string;
  title: string;
  icon: any;
  description: string;
  completed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onComplete: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(
      'bg-card border rounded-xl overflow-hidden transition-all',
      completed ? 'border-green-500/30' : 'border-border'
    )}>
      <div
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-muted/30 transition-smooth"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onComplete(); }}
            className="flex-shrink-0"
          >
            {completed ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          <Icon className="h-5 w-5 text-primary" />
          <div>
            <h3 className={cn('font-semibold text-sm', completed && 'line-through text-muted-foreground')}>{title}</h3>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      {expanded && (
        <div className="px-5 pb-5 pt-0 border-t border-border">
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

function TaxActTip({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
      <p className="text-xs font-medium text-primary mb-1">TaxAct Tip</p>
      <p className="text-xs text-muted-foreground">{children}</p>
    </div>
  );
}
